/* eslint-disable no-restricted-globals */
import axios, {
    type AxiosError,
    type AxiosInstance,
    type AxiosResponse,
    type InternalAxiosRequestConfig,
    type RawAxiosRequestHeaders
} from "axios"
import axiosRetrier from "axios-retry"
import isEmpty from "lodash-es/isEmpty"

import { ENotificationType, notificationService } from "@/3514/services"
import {
    captureJwtAxiosErrorWithSentry,
    captureNetworkAxiosErrorWithSentry,
    isNetworkGeneralError,
    isNetworkTimeoutError,
    seconds
} from "@/3514/utils"
import Urls from "@/api/urls"
import { handle401Error as prevent401Error } from "@/utils/handle-401-error"

import {
    getCurrentAccessToken,
    getCurrentRefreshToken,
    isAccessTokenValid,
    isRefreshTokenValid,
    setCurrentAccessToken,
    setCurrentRefreshToken
} from "./utils/tokens"

const PUBLIC_URLS: string[] = ["/api/respondent/survey-assignment/"]

const maxRetries: number = 3

const headers: RawAxiosRequestHeaders = { "X-Requested-With": "XMLHttpRequest" }

const xsrfCookieName: string = "csrftoken"
const xsrfHeaderName: string = "X-CSRFToken"

const axiosClient: AxiosInstance = axios.create({
    xsrfCookieName,
    xsrfHeaderName,
    withXSRFToken: true,
    headers,
    timeout: seconds(30)
})

axiosRetrier(axiosClient, {
    retries: maxRetries,
    retryDelay: (): number => seconds(3),
    retryCondition: (error: AxiosError): boolean => error.code === "ERR_NETWORK" || error.code === "ECONNABORTED"
})

let isRefreshing: boolean = false
let accessTokenPromise: Promise<string> | null = null

async function refreshAccessToken(): Promise<string | null> {
    if (isRefreshing) return accessTokenPromise

    isRefreshing = true

    try {
        const { data }: AxiosResponse = await axios.post(
            Urls.postTokenRefresh(),
            { refresh: getCurrentRefreshToken() },
            { headers, xsrfHeaderName, xsrfCookieName }
        )

        const newToken: string = data.access

        setCurrentAccessToken(newToken)

        return newToken
    } catch (error) {
        captureJwtAxiosErrorWithSentry(error)

        return null
    } finally {
        isRefreshing = false
    }
}

async function refreshTokenPair(): Promise<void> {
    if (isRefreshing) return void 0

    isRefreshing = true

    try {
        const { data }: AxiosResponse = await axios.post(
            Urls.postTokenPairRefresh(),
            { refresh: getCurrentRefreshToken() },
            {
                headers: { ...headers, Authorization: `Bearer ${getCurrentAccessToken()}` },
                xsrfHeaderName,
                xsrfCookieName
            }
        )

        const newAccessToken: string = data.access
        const newRefreshToken: string = data.refresh

        setCurrentAccessToken(newAccessToken)
        setCurrentRefreshToken(newRefreshToken)

        return void 0
    } catch (error) {
        captureJwtAxiosErrorWithSentry(error)

        prevent401Error()

        return void 0
    } finally {
        isRefreshing = false
    }
}

axiosClient.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
        if (response.status >= 200 && response.status < 303 && response.headers?.location)
            location.href = response.headers.location

        return response
    },
    (error: AxiosError): Promise<never> => {
        const retryCount: number = error.config?.["axios-retry"]?.retryCount ?? 0
        const isTransientError: boolean = isNetworkGeneralError(error) || isNetworkTimeoutError(error)
        const isLastAttempt: boolean = retryCount >= maxRetries

        if (error.isAxiosError && isTransientError && isLastAttempt) captureNetworkAxiosErrorWithSentry(error)

        if (isTransientError && isLastAttempt)
            notificationService.notify({
                id: Date.now().toString(),
                type: ENotificationType.NetworkError
            })

        return Promise.reject(error)
    }
)

axiosClient.interceptors.request.use(
    async (request: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        if (request.data && request.data.type && request.data.type === "html") {
            let form: string = ""

            request.data.body?.forEach(
                (item): void => ((form += `${item.name}=${encodeURIComponent(item.value)}&`), void 0)
            )

            request.data = form
        }

        const token: string = getCurrentAccessToken()
        const isPublicUrl: boolean = PUBLIC_URLS.some((url: string) => request.url?.startsWith(url))

        if (!token && isPublicUrl) return request // WISER-6634

        if (token && !isAccessTokenValid()) {
            accessTokenPromise = refreshAccessToken()
            request.headers.Authorization = `Bearer ${await accessTokenPromise}`
        } else if (token) request.headers.Authorization = `Bearer ${token}`

        if (!isEmpty(getCurrentRefreshToken()) && !isRefreshTokenValid()) refreshTokenPair().then()

        return request
    },
    (error: AxiosError): Promise<never> => Promise.reject(error)
)

export { axiosClient }
